import { CancelToken } from 'axios';
import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { getTrainingExternalListByUserId, selectTrainingExternalList, trainingExternalListActions } from '../../store/entities/trainingExternalList';
import { selectIdentity } from '../../store/entities/identity';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { Grid } from '../common/grid/Grid';
import { SectionHeader } from '../common/SectionHeader';
import { TrainingExternalItem } from '../../models/external';
import { CellYesNo } from '../common/grid/CellYesNo';
import { useTranslation } from 'react-i18next';
import { getLangNameObj, joinStrings2 } from '../../utils/format';
import _ from 'lodash';
import { TrainingRunButton } from '../trainingRun/TrainingRunButton';

export const TrainingExternalList: FC = () => {
  const { t, i18n } = useTranslation();
  const identity = useAppSelector(selectIdentity);
  const trainingExternalList = useAppSelector(selectTrainingExternalList);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(trainingExternalListActions.reload(true));
  }, [dispatch]);

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getTrainingExternalListByUserId(identity.id, cancelToken));
  };

  const handleRenderData = (item: TrainingExternalItem) => {
    const trainingCompleted = (item.missingTrainingsOnInvitations?.length ?? 0) === 0;
    const questionnaireCompleted = trainingCompleted && (item.missingQuestionnairesOnInvitations?.length ?? 0) === 0;
    const showButton = item.supplierEmployeeUserId === identity.id;

    const allMissingNames = _.union(
      item.missingTrainingsOnInvitations?.map((x) => getLangNameObj(i18n, x)) ?? [],
      item.missingQuestionnairesOnInvitations?.map((x) => getLangNameObj(i18n, x)) ?? []
    );
    const anyInvitation =
      (item.missingTrainingsOnInvitations?.length ?? 0) !== 0 ||
      (item.missingQuestionnairesOnInvitations?.length ?? 0) !== 0 ||
      (item.completedTrainingsOnInvitations?.length ?? 0) !== 0;

    return (
      <>
        <QcsTableCell>{item.name}</QcsTableCell>
        <QcsTableCell>{item.lastName}</QcsTableCell>
        <QcsTableCell>{getLangNameObj(i18n, item.training)}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{joinStrings2(allMissingNames)}</QcsTableCell>
        <CellYesNo value={trainingCompleted} />
        <CellYesNo value={questionnaireCompleted} />
        <QcsTableCell align="right">
          {showButton && (
            <TrainingRunButton
              workers={{
                employeeId: item.supplierEmployeeId ?? '',
                name: item.name ?? '',
                lastName: item.lastName ?? '',
                email: item.email,
                activities: [],
                workplaces: [],
                requiredTrainings: item.trainingVersion ? [item.trainingVersion] : [],
              }}
              variant={questionnaireCompleted ? 'outlined' : 'contained'}
              disabled={!anyInvitation}
            >
              {t(questionnaireCompleted ? 'trainingExternal.runTrainingAgain' : 'trainingExternal.runTraining')}
            </TrainingRunButton>
          )}
        </QcsTableCell>
      </>
    );
  };

  return (
    <>
      <SectionHeader title="trainingExternal.title" />
      <Grid<TrainingExternalItem>
        headers={[
          { captionStr: 'trainingExternal.name' },
          { captionStr: 'trainingExternal.lastName' },
          { captionStr: 'trainingExternal.training' },
          { captionStr: 'trainingExternal.invitation', hideOnMobile: true },
          { captionStr: 'trainingExternal.trainingCompleted' },
          { captionStr: 'trainingExternal.questionnaireCompleted' },
          {},
        ]}
        data={trainingExternalList}
        gridActions={trainingExternalListActions}
        renderData={handleRenderData}
        getData={handleGetData}
        search={false}
      />
    </>
  );
};